import { Chart } from 'react-chartjs-2';
import type { Chart as ChartType } from 'chart.js';
import React, { useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import { GRAPH_DEFAULT_OPTIONS, chartBorderPlugin } from './graphConfig';
import { middleLine } from './utils/graphPlugins';
import { useDispatchEventToChart } from './common/Graphs/hooks/useDispatchEventToChart';
import { palette } from 'theme/palette';
import { pxToRem } from 'src/common/utils/fonts';

export type PoolingChartEntryData = {
  x: Date;
  y: string;
  color?: string;
};

export type RSimSessionGraphRangeProps = {
  entries: PoolingChartEntryData[];
  from: number;
  to: number;
  min: number;
  max: number;
  onZoomComplete: (range: [Date, Date]) => void;
  onPan?: (newMin: Date, newMax: Date) => void;
};

export const PoolingGraph: React.FC<RSimSessionGraphRangeProps> = React.memo(
  ({ entries, from, to, onZoomComplete, onPan, min, max }) => {
    const barOptions = useMemo(
      () => ({
        ...GRAPH_DEFAULT_OPTIONS,
        animation: false,
        parsing: false,
        scales: {
          x: {
            ...GRAPH_DEFAULT_OPTIONS!.scales!.x,
            type: 'time',
            min: new Date(from),
            max: new Date(to),
          },
          y: {
            display: false,
            beginAtZero: false,
            min: -1,
            max: 1,
          },
        },
        plugins: {
          ...GRAPH_DEFAULT_OPTIONS!.plugins,
          tooltip: {
            enabled: false,
          },
          decimation: {
            enabled: false,
            algorithm: 'lttb',
            samples: 2000,
          },
          zoom: {
            ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom,
            limits: {
              ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.limits,
              x: {
                // @ts-expect-error -- add peak chart options type to options
                ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.limits.x,
                min: min,
                max: max,
              },
            },
            zoom: {
              ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.zoom,
              // @ts-expect-error -- add peak chart options type to options
              onZoom(context) {
                onZoomComplete([
                  new Date(context.chart.scales.x.min),
                  new Date(context.chart.scales.x.max),
                ]);
              },
            },
            pan: {
              ...GRAPH_DEFAULT_OPTIONS!.plugins?.zoom?.pan,
              mode: 'x',
              scaleMode: 'x',

              threshold: 200,
              // @ts-expect-error -- add peak chart options type to options
              onPanStart: (_event) => {},
              // @ts-expect-error -- add peak chart options type to options
              onPan: (event) => {
                if (onPan) {
                  const min = new Date(event.chart.boxes[0].min);
                  const max = new Date(event.chart.boxes[0].max);
                  onPan(min, max);
                }
              },
            },
          },
          middleLine: {
            borderColor: palette.grey['400'],
            borderWidth: 1,
          },
        },
      }),
      [from, to],
    );

    const data = useMemo(() => {
      const nacks = entries.filter((entry) => entry.color === palette.amber['600']);
      return {
        datasets: [
          {
            data: [...nacks],
            borderColor: palette.amber['600'],
            borderWidth: 0.2,
          },
          {
            data: [...entries],
            borderWidth: 2,
          },
        ],
      };
    }, [entries]);

    const chartRef = useRef<ChartType>(undefined);
    useDispatchEventToChart(chartRef);

    return (
      <Box sx={{ height: pxToRem(100) }}>
        <Chart
          // @ts-expect-error -- match ref type and chart and data types
          ref={chartRef}
          // @ts-expect-error -- add peak as a chart type
          type="peak"
          // @ts-expect-error -- add peak chart options type to options
          options={barOptions}
          data={data}
          plugins={[chartBorderPlugin, middleLine]}
        />
      </Box>
    );
  },
);
